import {
  Children,
  type ComponentProps,
  cloneElement,
  type DOMAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  useRef,
} from "react"

import {
  Tooltip as AriaTooltip,
  type TooltipProps as AriaTooltipProps,
  TooltipTrigger as AriaTooltipTrigger,
  Focusable,
  OverlayArrow,
  type TooltipTriggerComponentProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"

export type Placement = AriaTooltipProps["placement"]

export type TooltipProps = {
  /** The trigger element for the tooltip. For now, should be a react-aria
   * Button, but in the future we could consider using useTrigger and
   * useTooltipTrigger in the future. */
  children: ReactNode
  content: ReactNode
  delay?: TooltipTriggerComponentProps["delay"]
  showArrow?: boolean
  /** Props to pass to the tooltip popover */
  tooltipProps?: Omit<AriaTooltipProps, "className" | "placement"> & {
    className?: string
    placement?: Placement
  }
  /** Props to pass to the tooltip trigger */
  triggerProps?: Partial<TooltipTriggerComponentProps>
}

// FocusableElement is copied from @react-types/shared
interface FocusableElement extends Element, HTMLOrSVGElement {}
export type CustomTriggerChildren = ReactElement<
  DOMAttributes<FocusableElement>,
  string
>

/**
 * Allows for using arbitrary elements as the trigger for a tooltip. See
 * example:
 * https://react-spectrum.adobe.com/react-aria/Tooltip.html#custom-trigger
 *
 * Wrapped elements must
 * - Be vanilla HTML elements or a custom component which passes through a ref
 * - Be focusable - having a `tabIndex` attribute or being an interactive
 *   element (button, input, a, etc.)
 * - Have an interactive role (button, link, etc.). By default, the role will be
 *   set to `img` if not specified on the child. Full list of valid elements and
 *   roles can be found
 *   [here](https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useFocusable.tsx).
 *
 * `Tooltip` needs this component's `ref`. Put your own on the wrapped element.
 */
const CustomTrigger = (
  props: Omit<ComponentProps<typeof Focusable>, "ref">
) => {
  if (isValidElement(props.children) && Children.count(props.children) === 1) {
    const { children: child, ...rest } = props
    // Default role to img if not specified on child
    const role = child.props.role || "img"

    const clonedChild = cloneElement(child, { role }) as typeof child
    return <Focusable {...rest}>{clonedChild}</Focusable>
  }

  return <Focusable {...props} />
}

/**
 * A popup that appears when a user hovers or focuses on an element. Useful for
 * providing additional context or information about an element such as a button
 * or icon in a way that doesn't clutter the UI.
 *
 * ```tsx
 * import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
 *
 * <Tooltip content="Learn more">
 *   <IconButton icon={Info} label="Learn more" />
 * </Tooltip>
 * ```
 */

export const Tooltip = ({
  children,
  content,
  delay = 400,
  showArrow,
  tooltipProps: { className: tooltipClassName, ...tooltipProps } = {},
  triggerProps,
}: TooltipProps) => {
  // Anchor to the element `Tooltip.Trigger` wraps. react-aria keeps its own
  // trigger ref in a context that every focusable descendant overwrites, so a
  // Checkbox in the trigger moves the tooltip onto its hidden input (the icon).
  const anchorRef = useRef<FocusableElement | null>(null)
  const customTrigger =
    isValidElement<{ ref?: Ref<FocusableElement> }>(children) &&
    children.type === CustomTrigger
      ? children
      : null

  return (
    <AriaTooltipTrigger delay={delay} {...triggerProps}>
      {customTrigger
        ? cloneElement(customTrigger, { ref: anchorRef })
        : children}
      <AriaTooltip
        className={twMerge("tt-tooltip", tooltipClassName)}
        // `undefined` keeps react-aria's ref for a child that is its own trigger.
        triggerRef={customTrigger ? anchorRef : undefined}
        {...tooltipProps}
      >
        {showArrow && (
          <OverlayArrow>
            <svg
              className="tt-overlay-arrow"
              width={8}
              height={8}
              viewBox="0 0 8 8"
            >
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </OverlayArrow>
        )}
        {content}
      </AriaTooltip>
    </AriaTooltipTrigger>
  )
}

Tooltip.Trigger = CustomTrigger
